import { Link } from 'react-router-dom'
import { LuArrowRight, LuCircleCheck, LuClock, LuGraduationCap, LuRadio } from 'react-icons/lu'

/** In-app navigation when `to` is set (education); full-page cross-app link via `href` otherwise. */
function CardLink({ to, href, className, title, children }) {
  return to ? (
    <Link to={to} className={className} title={title}>
      {children}
    </Link>
  ) : (
    <a href={href} className={className} title={title}>
      {children}
    </a>
  )
}

const ICON_BTN =
  'w-11 h-11 flex items-center justify-center border border-brand-borderLight bg-white hover:bg-brand-goldDim hover:border-brand-gold/40 rounded-xl text-brand-redAccent shrink-0 transition-colors duration-200'

/**
 * Suite-wide academy course card (the education catalog design).
 * Renders `decorateCourse()` output; pass `onCurriculum` where the host app
 * has a curriculum modal — without it the graduation-cap button links to the
 * course page instead.
 */
export default function CourseCard({ course, to, href, enrolled = false, onCurriculum }) {
  return (
    <div className="group bg-white border border-brand-borderLight/70 hover:border-brand-gold/50 rounded-2xl overflow-hidden flex flex-col justify-between shadow-[0_1px_3px_rgba(26,13,6,0.06)] hover:shadow-[0_20px_40px_-12px_rgba(107,29,47,0.18)] hover:-translate-y-1 transition-all duration-300">
      {/* Cover image */}
      <CardLink
        to={to}
        href={href}
        className="relative h-70 overflow-hidden block shrink-0"
      >
        <div
          className="absolute inset-0 bg-cover transition-transform duration-500 group-hover:scale-[1.07]"
          style={{ backgroundImage: `url('${course.image}')` }}
        />
        <div className="absolute inset-0 bg-linear-to-t from-black/70 via-black/5 to-transparent" />

        <div className="absolute top-4 left-4 flex flex-col gap-1.5">
          <span className="px-3 py-1 bg-brand-redAccent/95 backdrop-blur-sm text-brand-textCream text-[9px] font-bold tracking-widest rounded-full uppercase shadow-sm">
            ★ {course.tier}
          </span>
          {course.isLive && (
            <span className="px-3 py-1 bg-[#f4b301] text-brand-bg text-[9px] font-bold tracking-widest rounded-full uppercase flex items-center gap-1 shadow-sm w-fit">
              <LuRadio className="w-3 h-3" /> Live Session
            </span>
          )}
        </div>
        {enrolled && (
          <span className="absolute top-4 right-4 px-3 py-1 bg-emerald-500 text-white text-[9px] font-bold tracking-widest rounded-full uppercase flex items-center gap-1 shadow-sm">
            <LuCircleCheck className="w-3 h-3" /> Enrolled
          </span>
        )}

        <h3 className="absolute bottom-3 left-4 right-4 text-lg font-serif font-bold tracking-wide text-white leading-snug line-clamp-2 drop-shadow-sm">
          {course.title}
        </h3>
      </CardLink>

      {/* Body */}
      <div className="p-5 space-y-4 flex-1 flex flex-col">
        <div className="flex items-center gap-2 text-[11px] font-semibold tracking-wider text-gray-500">
          <span className="flex items-center gap-1">
            <LuClock className="w-3.5 h-3.5 text-brand-gold" /> {course.duration}
          </span>
        </div>

        {course.instructors?.length > 0 && (
          <p
            className="flex items-center gap-1.5 text-[11px] font-semibold tracking-wider text-gray-500"
            title={course.instructors.join(', ')}
          >
            <LuGraduationCap className="w-3.5 h-3.5 text-brand-gold shrink-0" />
            <span className="truncate">{course.instructors.join(', ')}</span>
          </p>
        )}

        <p className="text-sm text-gray-600 font-light leading-relaxed line-clamp-2 flex-1">{course.desc}</p>

        <div className="flex flex-wrap gap-1.5">
          {course.certified && (
            <span className="px-2.5 py-1 bg-brand-goldDim border border-brand-gold/25 text-[10px] font-bold text-[#a87a30] rounded-full uppercase tracking-wide">
              Certified
            </span>
          )}
          <span className="px-2.5 py-1 bg-brand-redAccent/[0.06] border border-brand-redAccent/20 text-[10px] font-bold text-brand-redAccent rounded-full uppercase tracking-wide">
            {course.tierTag}
          </span>
        </div>

        <div className="flex items-center gap-2.5 pt-1">
          <CardLink
            to={to}
            href={href}
            className="flex-1 text-center py-3 bg-brand-redAccent hover:bg-[#521321] text-brand-textCream text-xs font-bold tracking-widest rounded-xl flex items-center justify-center gap-1.5 transition-all duration-200 hover:shadow-lg hover:shadow-brand-redAccent/20"
          >
            <span>{enrolled ? 'CONTINUE LEARNING' : course.ctaLabel}</span>
            <LuArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5" />
          </CardLink>

          {/* {onCurriculum ? (
            <button type="button" onClick={onCurriculum} className={ICON_BTN} title="Curriculum Overview">
              <LuGraduationCap className="w-5 h-5" />
            </button>
          ) : (
            <CardLink to={to} href={href} className={ICON_BTN} title="Curriculum Overview">
              <LuGraduationCap className="w-5 h-5" />
            </CardLink>
          )} */}
        </div>
      </div>
    </div>
  )
}
